import { createEvent, createStore, sample } from 'effector';
import {
  preferencesPatched,
  preferencesSaveFailed,
  preferencesSaved,
  type Preferences,
} from '../../entities/preferences';
import { toastShown } from '../../entities/toast';
import { ApiError } from '../../shared/api/client';
import { i18n } from '../../shared/lib/i18n';

/** A setting changed on this page: saved, then confirmed with a toast. */
export const settingChanged = createEvent<Partial<Preferences>>();

sample({ clock: settingChanged, target: preferencesPatched });

/** This page made a change that hasn't settled yet (other saves, like the language, stay quiet). */
const $awaiting = createStore(false).on(settingChanged, () => true);

const saved = sample({ clock: preferencesSaved, source: $awaiting, filter: Boolean });
const failed = sample({
  clock: preferencesSaveFailed,
  source: $awaiting,
  filter: (awaiting) => awaiting,
  fn: (_, error) => error,
});
$awaiting.reset(saved, failed);

sample({
  clock: saved,
  fn: () => ({ message: i18n.t('settings.saved'), tone: 'neutral' as const }),
  target: toastShown,
});
sample({
  clock: failed,
  fn: (error) => ({
    message:
      error instanceof ApiError
        ? (Object.values(error.fields)[0] ?? error.message)
        : i18n.t('errors.generic'),
    tone: 'error' as const,
  }),
  target: toastShown,
});
